import cookie, { type CookieOptions } from "cookiejs";

const TOKEN_KEY = "da_auth_token";
const USER_KEY = "da_auth_user";
const TOKEN_TTL_DAYS = 7;

export interface AuthSessionInput {
  token: string;
  id: number;
  email: string;
  full_name: string;
}

export interface StoredUser {
  id: number;
  email: string;
  full_name: string;
}

const baseOptions: CookieOptions = {
  expires: TOKEN_TTL_DAYS,
  path: "/",
  sameSite: "Lax",
  secure: typeof window !== "undefined" && window.location.protocol === "https:",
};

function readCookie(key: string): string | null {
  const value = cookie.get(key);
  return typeof value === "string" && value.length > 0 ? value : null;
}

export function setAuthSession(input: AuthSessionInput): void {
  cookie.set(TOKEN_KEY, input.token, baseOptions);
  const user: StoredUser = {
    id: input.id,
    email: input.email,
    full_name: input.full_name,
  };
  cookie.set(USER_KEY, JSON.stringify(user), baseOptions);
}

export function updateStoredUserName(full_name: string): void {
  const raw = readCookie(USER_KEY);
  if (!raw) return;
  try {
    const user = JSON.parse(raw) as StoredUser;
    cookie.set(USER_KEY, JSON.stringify({ ...user, full_name }), baseOptions);
  } catch {
    // ignore
  }
}

export function getAuthToken(): string | null {
  return readCookie(TOKEN_KEY);
}

export function getAuthUser(): StoredUser | null {
  const raw = readCookie(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as StoredUser;
  } catch {
    return null;
  }
}

export function isAuthenticated(): boolean {
  return getAuthToken() !== null;
}

interface JwtPayload {
  user_id?: number;
  email?: string;
  role?: string;
  exp?: number;
}

export function getAuthRole(): string | null {
  const token = getAuthToken();
  if (!token) return null;
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const b64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const decoded = JSON.parse(atob(padded)) as JwtPayload;
    return decoded.role ?? null;
  } catch {
    return null;
  }
}

export function clearAuthSession(): void {
  cookie.remove([TOKEN_KEY, USER_KEY]);
}
